import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getLanguageUrl, getToolKeyFromUrl } from '../../utilities/languageUrls';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

export function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: string) => {
    // Get the current tool key from the URL
    const toolKey = getToolKeyFromUrl(location.pathname);
    
    // Generate the new URL for the selected language
    let newUrl: string;
    if (toolKey) {
      // If we're on a tool page, navigate to the same tool in the new language
      newUrl = getLanguageUrl(languageCode as any, toolKey);
    } else {
      // If we're on the home page or root, navigate to the home page in the new language
      newUrl = `/${languageCode}`;
    }
    
    // Navigate to the new URL
    navigate(newUrl);
    
    // Also update the i18n language
    i18n.changeLanguage(languageCode);
    
    handleClose();
  };

  // Mobile version - just icon button
  if (isMobile) {
    return (
      <Box>
        <Tooltip title={t('languageSelector.title') || 'Change Language'}>
          <IconButton
            onClick={handleClick}
            sx={{
              color: '#aedff2',
              '&:hover': {
                backgroundColor: 'rgba(174, 223, 242, 0.1)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span style={{ fontSize: '16px' }}>{currentLanguage.flag}</span>
              <LanguageIcon sx={{ fontSize: '20px' }} />
            </Box>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              backgroundColor: '#282B39',
              border: '1px solid rgba(174, 223, 242, 0.2)',
              borderRadius: '12px',
              mt: 1,
              maxHeight: '60vh',
              overflow: 'auto',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {languages.map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              selected={i18n.language === language.code}
              sx={{
                color: '#aedff2',
                '&:hover': {
                  backgroundColor: 'rgba(174, 223, 242, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 217, 168, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 217, 168, 0.15)',
                  },
                },
                minWidth: '160px',
                py: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <span style={{ fontSize: '18px' }}>{language.flag}</span>
                <Typography variant="body2" sx={{ color: '#aedff2', flex: 1 }}>
                  {language.name}
                </Typography>
                {i18n.language === language.code && (
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#FFD9A8',
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }

  // Desktop version - full button with text
  return (
    <Box>
      <Tooltip title={t('languageSelector.title') || 'Change Language'}>
        <Button
          onClick={handleClick}
          startIcon={<LanguageIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            color: '#aedff2',
            textTransform: 'none',
            fontSize: '14px',
            '&:hover': {
              backgroundColor: 'rgba(174, 223, 242, 0.1)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '16px' }}>{currentLanguage.flag}</span>
            <Typography variant="body2" sx={{ color: '#aedff2' }}>
              {currentLanguage.name}
            </Typography>
          </Box>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: '#282B39',
            border: '1px solid rgba(174, 223, 242, 0.2)',
            borderRadius: '12px',
            mt: 1,
            maxHeight: '60vh',
            overflow: 'auto',
          },
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={i18n.language === language.code}
            sx={{
              color: '#aedff2',
              '&:hover': {
                backgroundColor: 'rgba(174, 223, 242, 0.1)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 217, 168, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 217, 168, 0.15)',
                },
              },
              minWidth: '180px',
              py: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <span style={{ fontSize: '18px' }}>{language.flag}</span>
              <Typography variant="body2" sx={{ color: '#aedff2', flex: 1 }}>
                {language.name}
              </Typography>
              {i18n.language === language.code && (
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#FFD9A8',
                  }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
